import { Web3Provider } from '@ethersproject/providers';
import * as ethers from 'ethers';
import React, { Dispatch } from 'react';
import debug from 'debug';
import { ConditionWaiter } from '../../common/ConditionWaiter';
import { UserAccount } from '../../common/UserAccount';
import { Action } from '../../context/GenerateContext';
import { UserService, WithdrawStatus } from './UserService';
import { UserActionType } from './UserActionType';
import { UserPermission } from '../../common/UserPermission';
import { LoginState, loginStateFinished, loginStateInProgress } from '../../common/LoginState';
import { environment } from '../../environments/environment';
import { noChainId } from '../../common/ProviderUtils';
import { AnalyticsEventName } from '../../services/analytics/AnalyticsEventName';
import { mainSuite } from '../../services/ServiceFactory';
import { ChainalysisService } from '../chainalysis/ChainalysisService';

const log = debug('app:service:MetaMaskUserService');

export class MetaMaskUserService implements UserService {
  readonly logInPopUp = true;

  private initWaiter = new ConditionWaiter(false);
  private loginStateWaiter = new ConditionWaiter(LoginState.Idle, loginStateFinished);
  private userDispatchWaiter = new ConditionWaiter<React.Dispatch<Action>>(null);
  private userStateWaiter = new ConditionWaiter<UserAccount>(null);
  private providerWaiter = new ConditionWaiter<Web3Provider>(null);
  private signer: ethers.Signer;
  private address: string;
  private chainId = noChainId;

  constructor(
    private chainalysisService: ChainalysisService
  ) {
    this.initLoggedInUser();
  }

  hasInitialized(): boolean {
    return this.initialized;
  }
  waitForInit(): Promise<void> {
    return this.initWaiter.wait() as unknown as Promise<void>;
  }

  setDispatch(dispatch: Dispatch<Action>): void {
    this.userDispatchWaiter.set(dispatch);
    this.checkForLoginState();
    this.checkForInit();
  }
  setUserState(state: UserAccount): void {
    this.userStateWaiter.set(state);
    this.checkForLoginState();
    this.checkForInit();
  }
  signUp(): void {
    this.startLogin();
  }
  logIn(): void {
    this.startLogin();
  }
  logOut(): void {
    this.startLogout(false);
  }

  async getSigner(): Promise<any> {
    await this.waitForInit();
    if (!this.userState.loggedIn) {
      return null;
    }
    return this.provider.getSigner();
  }

  async getTransactionReceipt(txHash: string | Promise<string>): Promise<any> { 
    await this.waitForInit();
    if (!this.userState.loggedIn) {
      return null;
    }
    return await this.provider.getTransactionReceipt(txHash);
  }


  async checkIfLinked(force?: boolean): Promise<boolean> {
    await this.waitForInit();
    if (!this.userState.loggedIn) { return false; }
    if (!force && this.userState.checked) { return this.userState.linked; }

    const linked = true;

    this.userDispatch({ type: UserActionType.UpdateLinked, linked });
    this.userDispatch({ type: UserActionType.UpdateChecked, checked: true });
    this.userDispatch({ type: UserActionType.UpdateName, name: this.address });
    this.userDispatch({ type: UserActionType.UpdateAddress, address: this.address });

    return linked;
  }
  async maybeLink(): Promise<void> {
    const linked = await this.checkIfLinked();

    if (linked) { return; }

    return this.link();
  }
  async link(): Promise<void> {
    // TODO do we do any actual linking?
    await this.checkIfLinked(true);
  }
  getServiceAddress(): Promise<string> {
    return Promise.resolve(environment.serviceAddress);
  }
  async fetchChainBalance(): Promise<string> {
    if (!this.provider) { return; }
    if (!this.signer) {
      throw new Error('Not logged in');
    }
    
    const balanceBN = await this.signer.getBalance();
    return ethers.utils.formatEther(balanceBN);
  }

  private get initialized(): boolean {
    return this.initWaiter.get();
  }

  private get userDispatch(): React.Dispatch<Action> {
    return this.userDispatchWaiter.get();
  }

  private get userState(): UserAccount {
    return this.userStateWaiter.get();
  }

  private get loggedIn(): boolean {
    return this.loginState === LoginState.LoggedIn;
  }

  private get loggedOut(): boolean {
    return this.loginState === LoginState.LoggedOut;
  }

  private get provider(): Web3Provider {
    return this.providerWaiter.get();
  }

  private get loginState(): LoginState {
    return this.loginStateWaiter.get();
  }

  private waitForProvider(): Promise<Web3Provider> {
    return this.providerWaiter.wait();
  }

  private waitForLoginState(): Promise<LoginState> {
    return this.loginStateWaiter.wait();
  }

  private async waitForStateAndDispatch(): Promise<void> {
    await Promise.all([
      this.userDispatchWaiter.wait(),
      this.userStateWaiter.wait(),
    ]);
  }

  private async isMetaMaskConnected(): Promise<boolean> {
    if (!this.provider) { return false; }
    const accounts = await this.provider.listAccounts();
    return accounts.length > 0;
  }

  private checkForInit(): void {
    if (this.initialized) { return; }
    if (!this.userState) { return; }
    if (!this.userDispatch) { return; }
    if (!loginStateFinished(this.loginState)) { return; }

    this.initWaiter.set(true);
  }

  private checkForLoginState(): void {
    if (!this.userState) { return; }
    if (!this.initialized && !loginStateInProgress(this.loginState)) { return; }

    this.loginStateWaiter.set(
      this.userState.loggedIn ? LoginState.LoggedIn : LoginState.LoggedOut
    );
  }

  private async initLoggedInUser() {
    if (!this.provider && window.ethereum) {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      this.providerWaiter.set(provider);
      provider.on('network', (_newNetwork, oldNetwork) => {
        if (oldNetwork) {
          this.loginStateWaiter.set(LoginState.Idle);
          this.initLoggedInUser();
        }
      });
      window.ethereum.on('chainChanged', () => {
        this.providerWaiter.set(null);
        this.loginStateWaiter.set(LoginState.Idle);
        this.initLoggedInUser();
      });
      window.ethereum.on('accountsChanged', (accounts: any[]) => {
        if (!(accounts?.length) || this.initialized) {
          this.loginStateWaiter.set(LoginState.Idle);
          this.initLoggedInUser();
        }
      });
    }

    const connected = await this.isMetaMaskConnected();
    let loggedIn = false;
    if (connected) {
      log('MetaMask connected');
      const signer = this.provider.getSigner();
      if (signer) {
        loggedIn = true;
        this.loginStateWaiter.set(LoginState.LoggingIn);
        await this.logInAsSigner(signer);
        // userDispatch should eventually trigger checkForLoginState
      }
    } else {
      log('MetaMask not connected');
    }
    if (!loggedIn) {
      this.startLogout(true);
    }
  }
  
  private async startLogin(): Promise<void> {
    mainSuite.analyticsService.track(AnalyticsEventName.WalletConnectStart);
    this.loginStateWaiter.set(LoginState.LoggingIn);
    await this.waitForInit();
    const provider = await this.waitForProvider();
    try {
      await provider.send('eth_requestAccounts', []);
      // this triggers accountsChanged event
      // initLoggedInUser should take care of things
    } catch (e) {
      log('Error occurred while logging in:', e);
      mainSuite.analyticsService.track(AnalyticsEventName.WalletConnectError, {
        errorCode: e.code,
        errorMessage: e.message,
        errorStack: e.stack
      });
      this.loginStateWaiter.set(LoginState.LoggedOut);
    }
  }

  private async logInAsSigner(signer: ethers.Signer): Promise<void> {
    await this.waitForStateAndDispatch();
    this.signer = signer;
    this.address = await this.signer.getAddress();
    const serviceAddress = await this.getServiceAddress();
    this.chainId = await this.signer.getChainId();
    const caSafe = await this.chainalysisService.isWalletAddressSafe(this.address);
    const permissions = [ UserPermission.User ];
    if (this.address === serviceAddress) {
      permissions.push(UserPermission.Admin);
      permissions.push(UserPermission.Service);
    }
    // this should eventually call checkForLoginState
    this.userDispatch({
      type: UserActionType.LogIn,
      caSafe,
      chainId: this.chainId,
      address: this.address,
      name: this.address,
      permissions,
    });
  }

  private async startLogout(skipWait: boolean): Promise<void> {
    await this.waitForStateAndDispatch();
    this.loginStateWaiter.set(LoginState.LoggingOut);
    this.signer = null;
    this.address = null;
    this.userDispatch({ type: UserActionType.LogOut });
  }
}
